<script>
  import { fade } from "svelte/transition";
  import { FADE_DURATION } from "../consts";
  import minimize from "@services/minimize";
  import { featureEnabled } from "@stores/features";
  import {
    allowedQuestionsOnScreen
  } from "@stores/features/allowed-questions";
  import { shown as controlsShown } from "@stores/features/hide-controls";
  import {
    suggestedQuestionsSheetOpen,
    suggestedQuestionsOnScreen
  } from "@stores/features/suggested-questions";
  import {
    displayTooltip,
    introSeen,
    maximized,
    microphoneAvailable,
    textMode,
    voiceMode
  } from "@stores/global";
  import { showPaywall, showUserSheet } from "@stores/sheets";
  import {
    hasHistoryEntries,
    iframed,
    supportsEnhancedExperience,
    supportsVoice
  } from "@utils/support";
  import AllowedQuestions from "@components/features/AllowedQuestions";
  import CloseButton from "@components/CloseButton";
  import ErrorNotification from "@components/ErrorNotification";
  import ExperienceTypeToggle from "@components/ExperienceTypeToggle";
  import Form from "@components/Form";
  import Links from "@components/features/Links";
  import Paywall from "@components/features/Paywall";
  import StartButton from "@components/StartButton";
  import SuggestedQuestions from "@components/features/SuggestedQuestions";
  import TalkButton from "@components/TalkButton";
  import Tooltip from "@components/Tooltip";
  import UserButton from "@components/UserButton";

  $: displayBack = !iframed && hasHistoryEntries;
  $: displayClose = iframed && (!supportsEnhancedExperience || $maximized);

  $: displayControls =
    !supportsEnhancedExperience || ($controlsShown && $introSeen && $maximized);

  $: displayForm = !supportsVoice || (displayControls && $textMode);
  $: displayVoiceControls = supportsVoice && displayControls && $voiceMode;

  // Disables form in kiosk mode etc
  const textEntryDisabled = featureEnabled("disable-text");

  /**
   * Navigate back
   */
  function goBack() {
    window.history.go(-1);
  }
</script>

<style>
  .container {
    background: var(--black);
    box-sizing: border-box;
    padding: 1.5rem 2rem;
    position: relative;
    width: 100%;
  }

  :global(.enhanced) .container {
    background: transparent;
    height: 100%;
    left: 0;
    min-width: 28rem;
    overflow: hidden;
    padding: 0 0 2rem;
    position: absolute;
    z-index: 2;
  }
</style>

{#if displayBack}
  <CloseButton inverted={true} onClick={goBack} style="right: 1rem; top: 1rem; z-index: 3" />
{:else if displayClose}
  <CloseButton inverted={true} onClick={minimize} style="right: 1rem; top: 1rem; z-index: 3" />
{/if}

{#if displayControls}
  <div
    class="container"
    transition:fade={{ duration: FADE_DURATION }}
  >
    <ErrorNotification />

    {#if featureEnabled("links")}
      <Links />
    {/if}

    {#if $showPaywall}
      <Paywall />
    {:else}
      {#if $displayTooltip && $voiceMode && !$showUserSheet}
        <Tooltip />
      {/if}

      {#if supportsEnhancedExperience && featureEnabled("paywall") }
        <UserButton />
      {/if}

      {#if (
        !$allowedQuestionsOnScreen &&
        !$suggestedQuestionsSheetOpen &&
        !$showUserSheet &&
        !textEntryDisabled
      )}
        {#if supportsVoice && $microphoneAvailable}
          <ExperienceTypeToggle />
        {/if}

        {#if displayForm}
          <Form />
        {/if}
      {/if}

      {#if displayVoiceControls && !$showUserSheet}
        <TalkButton />
      {/if}

      {#if !$showUserSheet}
        {#if featureEnabled("allowed-questions") && $allowedQuestionsOnScreen}
          <AllowedQuestions />
        {/if}

        {#if featureEnabled("suggested-questions") && $suggestedQuestionsOnScreen}
          <SuggestedQuestions />
        {/if}
      {/if}
    {/if}
  </div>
{:else if !$maximized}
  <StartButton />
{/if}
